import {useResizeObserver} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import {HomeDashboardStoreState} from 'home-dashboard/HomeDashboardStore';
import {isNavbarColored, makeNavbarBackgroundStyles} from 'nav/navbar/NavBarColor';
import NavBarOverflowMenu from 'nav/navbar/NavBarOverflowMenu';
import useOverflowingNavBarIcons from 'nav/navbar/useOverflowingNavBarIcons';
import AnchorModalsForOnboarding from 'onboarding/AnchorModalsForOnboarding';
import {ITEM_SIZE_SIDE_NAVBAR, ZINDEX_SIDE_NAVBAR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

import NavBarIcon from './NavBarIcon';

interface NavBarContentsProps {
  currentUser: EntityStoreUser;
  homeDashboardState: HomeDashboardStoreState;
  isCreateMenuOpen?: boolean;
  isOverflowMenuOpen?: boolean;
  isUserIconActive?: boolean;
  onSectionClick: (section: Section, e?: ElementMouseEvent) => void;
  section: Section | null;
}

export default function NavBarContents({
  currentUser,
  homeDashboardState,
  isCreateMenuOpen = false,
  isOverflowMenuOpen = false,
  isUserIconActive = false,
  onSectionClick,
  section,
}: NavBarContentsProps): JSX.Element {
  const [ref, rect] = useResizeObserver();

  const isHomeIconActive =
    section === SECTIONS.HOME ||
    (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS &&
      (section === SECTIONS.WORKLISTS || section === SECTIONS.WORKLIST_ITEMS));

  const {topIcons, collapsibleIconsProps, overflowIconsProps, bottomIcons} = useOverflowingNavBarIcons({
    currentUser,
    isCreateMenuOpen,
    isHomeIconActive,
    isUserIconActive,
    height: rect.height,
    onSectionClick,
    section,
  });

  return (
    <div
      className={classNames(
        css(navBarContentsStyles.navbar, window.impersonating && navBarContentsStyles.impersonating),
        'js-navTour-nav _test-navBarContents'
      )}
      style={makeNavbarBackgroundStyles()}
      {...getTestProps({component: 'NavBarContents', element: 'root'})}
      // This overflow attribute is used by Cypress to know which sections have been shunted to the overflow menu
      data-overflow={overflowIconsProps.map((icon) => icon.section).join(',')}
      ref={ref}
      role="tablist"
    >
      {...topIcons}
      {...collapsibleIconsProps.map((iconProps) => <NavBarIcon key={iconProps.section} {...iconProps} />)}
      {overflowIconsProps.length > 0 && (
        <NavBarOverflowMenu
          activeSection={section}
          isOverflowMenuOpen={isOverflowMenuOpen}
          sections={overflowIconsProps}
        />
      )}
      {/* Keyed so the stack keeps its identity as resizing moves icons in and out of the overflow
          menu; otherwise its child index shifts and React remounts it, dropping state such as an
          open notification panel. */}
      <div key="bottomStack" className={css(navBarContentsStyles.bottomStack)}>
        {...bottomIcons}
      </div>
      {homeDashboardState && <AnchorModalsForOnboarding {...homeDashboardState} />}
    </div>
  );
}

export const navBarContentsStyles = StyleSheet.create({
  navbar: {
    ...(!isNavbarColored() && {backgroundColor: Colors.BLUE}),
    bottom: 0,
    position: 'absolute',
    top: 0,
    width: ITEM_SIZE_SIDE_NAVBAR,
    zIndex: ZINDEX_SIDE_NAVBAR,
  },
  impersonating: {
    backgroundColor: Colors.RED_LIGHT,
  },
  // Pins the fixed-bottom icons to the bottom of the rail and stacks them in DOM order, so each
  // icon's slot is derived from the stack rather than a hardcoded `bottom` offset per icon.
  bottomStack: {
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    width: ITEM_SIZE_SIDE_NAVBAR,
  },
  disabledIcon: {
    color: Colors.WHITE,
    opacity: 0.4,
  },
  spacer: {
    height: 20,
  },
});
